import { useTranslation } from "react-i18next";
import { BrutalInput } from "@/components/ui/BrutalInput";
import {
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import { contractTypes, workModes, salaryPeriods } from "@/data/jobOptions";
import { JobFormData } from "@/pages/dashboard/JobCreate";

interface JobFormStep1Props {
  formData: JobFormData;
  updateFormData: (updates: Partial<JobFormData>) => void;
}

export function JobFormStep1({ formData, updateFormData }: JobFormStep1Props) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-xl font-bold text-dark mb-2">
          {t("jobs.form.step1Title")}
        </h2>
        <p className="text-muted-foreground">
          {t("jobs.form.step1Desc")}
        </p>
      </div>

      {/* Job Title */}
      <div>
        <label className="block font-semibold text-dark mb-2">
          {t("jobs.form.title")} *
        </label>
        <BrutalInput
          value={formData.title}
          onChange={(e) => updateFormData({ title: e.target.value })}
          placeholder={t("jobs.form.titlePlaceholder")}
        />
      </div>

      {/* Location */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block font-semibold text-dark mb-2">
            {t("jobs.form.city")} *
          </label>
          <BrutalInput
            value={formData.location_city}
            onChange={(e) => updateFormData({ location_city: e.target.value })}
            placeholder={t("jobs.form.cityPlaceholder")}
          />
        </div>
        <div>
          <label className="block font-semibold text-dark mb-2">
            {t("jobs.form.region")}
          </label>
          <BrutalInput
            value={formData.location_region}
            onChange={(e) => updateFormData({ location_region: e.target.value })}
            placeholder={t("jobs.form.regionPlaceholder")}
          />
        </div>
      </div>

      {/* Contract Type & Work Mode */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block font-semibold text-dark mb-2">
            {t("jobs.form.contractType")} *
          </label>
          <BrutalSelect
            value={formData.contract_type}
            onValueChange={(value) => updateFormData({ contract_type: value as any })}
          >
            <BrutalSelectTrigger>
              <BrutalSelectValue placeholder={t("jobs.form.selectContract")} />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              {contractTypes.map((c) => (
                <BrutalSelectItem key={c.value} value={c.value}>
                  {t(c.labelKey)}
                </BrutalSelectItem>
              ))}
            </BrutalSelectContent>
          </BrutalSelect>
        </div>
        <div>
          <label className="block font-semibold text-dark mb-2">
            {t("jobs.form.workMode")} *
          </label>
          <BrutalSelect
            value={formData.work_mode}
            onValueChange={(value) => updateFormData({ work_mode: value as any })}
          >
            <BrutalSelectTrigger>
              <BrutalSelectValue placeholder={t("jobs.form.selectWorkMode")} />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              {workModes.map((w) => (
                <BrutalSelectItem key={w.value} value={w.value}>
                  {t(w.labelKey)}
                </BrutalSelectItem>
              ))}
            </BrutalSelectContent>
          </BrutalSelect>
        </div>
      </div>

      {/* Salary */}
      <div>
        <label className="block font-semibold text-dark mb-2">
          {t("jobs.form.salary")} ({t("common.optional")})
        </label>
        <div className="grid md:grid-cols-3 gap-4">
          <BrutalInput
            type="number"
            value={formData.salary_range_min?.toString() || ''}
            onChange={(e) => updateFormData({ salary_range_min: e.target.value ? parseInt(e.target.value) : null })}
            placeholder={t("jobs.form.salaryMin")}
          />
          <BrutalInput
            type="number"
            value={formData.salary_range_max?.toString() || ''}
            onChange={(e) => updateFormData({ salary_range_max: e.target.value ? parseInt(e.target.value) : null })}
            placeholder={t("jobs.form.salaryMax")}
          />
          <BrutalSelect
            value={formData.salary_period || ''}
            onValueChange={(value) => updateFormData({ salary_period: value as any || null })}
          >
            <BrutalSelectTrigger>
              <BrutalSelectValue placeholder={t("jobs.form.salaryPeriod")} />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              {salaryPeriods.map((s) => (
                <BrutalSelectItem key={s.value} value={s.value}>
                  {t(s.labelKey)}
                </BrutalSelectItem>
              ))}
            </BrutalSelectContent>
          </BrutalSelect>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {t("jobs.form.salaryHint")}
        </p>
      </div>
    </div>
  );
}
